import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, CircularProgress, Typography } from "@mui/material";
import GroupedSelect from "components/control/GroupedSelect";
import {
  useGetVisBoxwhiskerMutation, useGetUserQuery, useGetVisBreakdownMutation,
  VisBreakdownOptions, VisBoxwhiskersOptions
} from "api/api";
import BoxPlot from "./BoxPlot";
import BarChart from "./BarChart";
import HeatMap from "./HeatMap";
import { YearRange } from "components/YearRangePicker/YearRangePicker";
import { User } from "types/types";
import {
  BarChartType, HeatMapType,
  DataPointType, FirmOptionType, FitToOptionType, IntensityMetricType, RawBoxwhiskersItem, RawBreakdownItem, TypeGroup,
  isBarChartType, isBoxPlotType, isHeatMapType, isMaterialBoxPlotType, isPerformanceOverTimeType,
} from "./types";
import ChartMenu from "components/control/ChartMenu";
import { AssessmentStageType, BoxPlotType, ChartType, ProjectListType } from "./types";

const LOW_DATA_MSG = 'There is not enough data to display an anonymized visualization of SE 2050 firm projects submitted to date for these filter settings. Please modify your filter selection or try a different visualization.';

const BLUE_COLOR = '#037FB0';
const RED_COLOR = '#FA1F14';

export const chartSelectStyles = {
  textAlign: "left",
  display: 'flex',
  gap: '1.75rem',
  paddingTop: '0.25rem',
  paddingBottom: '0.25rem',
  paddingRight: '0.25rem',
  paddingLeft: '1.25rem',
  marginTop: '1rem',
  backgroundColor: 'white',
  borderRadius: '1.5rem',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: '#78716c',
  '.MuiOutlinedInput-input': {
    padding: 0, // Ensures padding is removed
  },
  '.MuiSelect-icon': {
    marginTop: -1.1,
    marginRight: -0.75
  },
}

const transformBoxwhiskersData = (
  rawData: Record<string, RawBoxwhiskersItem[]>,
  assessmentStage: AssessmentStageType,
  projectListType: ProjectListType,
  userData: User | undefined,
  chartType: ChartType,
  firmHighlighted: string
): { group: string; value: number; n: number, displayed: boolean }[] => {
  if (!userData) return [];
  
  const calculateValue = (item: RawBoxwhiskersItem, stage: AssessmentStageType): number => {
    if (isMaterialBoxPlotType(chartType)) {
      if (!item.gross_area_sqft) {
        console.warn('Missing required gross_area_sqft value for datapoint.');
        return NaN;
      }
      return (item.quantity || 0) / item.gross_area_sqft;
    }

    if (!item.gross_area_sqm) {
      console.warn('Missing required gross_area_sqm value for datapoint.');
      return NaN;
    }

    let totalGWP = 0;
    switch (stage) {
      case "A1-A3":
        totalGWP = (item.a1_a3_gwp_kg || 0);
        break;
      case "A1-A4":
        totalGWP = (item.a1_a3_gwp_kg || 0) + (item.a4_gwp_kg || 0);
        break;
      case "A1-A5":
        totalGWP = (item.a1_a3_gwp_kg || 0) + (item.a4_gwp_kg || 0) + (item.a5_gwp_kg || 0);
        break;
      case "A-C":
        totalGWP = (item.a1_a3_gwp_kg || 0) + (item.a4_gwp_kg || 0) + (item.a5_gwp_kg || 0) +
          (item.b1_b5_gwp_kg || 0) + (item.c1_c4_gwp_kg || 0);
        break;
    }
    return totalGWP / item.gross_area_sqm;
  };

  const determineIfDotIsVisible = (item: RawBoxwhiskersItem): boolean => {
    if (isPerformanceOverTimeType(chartType)) return false;

    switch (userData.site_role) {
      case 'Super Admin':
        return true;
      case 'Sub Super Admin':
        return false;
      case 'Firm Admin':
        return item.is_firm_project;
      case 'Firm User':
        return item.is_firm_project;
      default:
        return false;
    }
  }

  const getDotColor = (item: RawBoxwhiskersItem): string => {
    switch (userData.site_role) {
      case 'Super Admin':
        return item.firm === firmHighlighted ? RED_COLOR : BLUE_COLOR;
      case 'Sub Super Admin':
        return BLUE_COLOR;
      case 'Firm Admin':
        return RED_COLOR;
      case 'Firm User':
        return RED_COLOR;
      default:
        return BLUE_COLOR;
    }
  }

  const determineIfDatumIsIncluded = (item: RawBoxwhiskersItem): boolean => {
    switch (userData.site_role) {
      case 'Super Admin':
        if (isPerformanceOverTimeType(chartType) || isHeatMapType(chartType)) {
          return !firmHighlighted || item.firm === firmHighlighted;
        }
        return true;
      case 'Sub Super Admin':
        return true;
      case 'Firm Admin':
      case 'Firm User':
        if (!isPerformanceOverTimeType(chartType) && !isHeatMapType(chartType)) return true;
        if (projectListType === 'All Projects') return true;
        return item.is_firm_project;
      default:
        return false;
    }
  }

  return Object.entries(rawData).flatMap(([group, items]) => {
    const filteredItems = items.filter(determineIfDatumIsIncluded);
    const transformedData = filteredItems.map(item => ({
      group: group,
      value: calculateValue(item, assessmentStage),
      n: filteredItems.length,
      displayed: determineIfDotIsVisible(item),
      color: getDotColor(item)
    }))

    if (userData.site_role === 'Super Admin' && firmHighlighted) {
      // show red dots on top
      transformedData.sort((a, b) => (a.color !== RED_COLOR) ? -1 : 1);
    }

    return transformedData;
  });
};

const transformBreakdownData = (
  rawData: RawBreakdownItem[],
  categoryKey: string,
  typeKey: string,
  firmHighlighted: string
): any[] => {
  
  const determineIfDatumIsIncluded =(item: RawBreakdownItem): boolean => {
    let included = true;
    if (firmHighlighted) included = included && item.firm === firmHighlighted;
    return included;
  }

  const filteredData = rawData.filter(determineIfDatumIsIncluded);

  return filteredData.reduce((acc, { name, data }) => {
    let newItem = { name };
    for (const datum of data) {
      if (datum[typeKey]) {
        const intensity = datum.intensity || 0;
        const absIntensity = Math.abs(intensity);
        let decimals = 0;
        if (absIntensity < 1) decimals = 3;
        else if (absIntensity < 10) decimals = 2;
        else if (absIntensity < 100) decimals = 1;
        const roundedValue = Math.round((intensity + Number.EPSILON) * (10 ** decimals)) / (10 ** decimals);
        newItem[`${datum[categoryKey]}: ${datum[typeKey]}`] = roundedValue;
      }
    }
    acc.push(newItem);
    return acc;
  }, [] as any[]);
}

const generateTypeGroups = (rawData: RawBreakdownItem[], categoryKey: string, typeKey: string): { category: string, subtypes: string[] }[] => {
  const subtypesByCategory = rawData.reduce((acc, { name, data }) => {
    for (const datum of data) {
      if (datum[typeKey] && datum[categoryKey]) {
        if (datum[categoryKey] in acc) {
          acc[datum[categoryKey]].add(`${datum[categoryKey]}: ${datum[typeKey]}`);
        } else {
          acc[datum[categoryKey]] = new Set([ `${datum[categoryKey]}: ${datum[typeKey]}` ]);
        }
      }
    }
    return acc;
  }, {});

  return Object.entries(subtypesByCategory)
               .sort((tg1, tg2) => {
                 if (categoryKey === 'overall_structure') {
                   if (tg1[0] === 'Superstructure') return -1;
                   if (tg2[0] === 'Superstructure') return 1;
                 }
                 return 0;
               })
               .map(([ overallStruct, structs ]): TypeGroup => {
                 return {
                   category: overallStruct,
                   subtypes: Array.from(structs as Set<string>).sort(orderSeries)
                 }
               });
}

const determineBreakdownTitle = (chartType: ChartType, intensityMetric: IntensityMetricType): string => {
  switch (chartType) {
    case 'Concrete Use Intensity by Element':
      return `Concrete ${intensityMetric} Intensity by Element`;
    case 'Steel Use Intensity by Element':
      return `Steel ${intensityMetric} Intensity by Element`;
    case 'Timber Use Intensity by Element':
      return `Timber ${intensityMetric} Intensity by Element`;
    default:
      return chartType as string;
  }
}

const determineBoxwhiskersTitle = (chartType: ChartType): string => {
  if (isPerformanceOverTimeType(chartType)) {
    return "Embodied Carbon Intensity, by Submission Year";
  } else if (isHeatMapType(chartType)) {
    return "Project Heat Map";
  } else if (isMaterialBoxPlotType(chartType)) {
    return `${getDataPointType(chartType)} Use Intensity`;
  } else {
    return `Embodied Carbon Intensity, by ${chartType}`;
  }
}

const determineBreakdownLegend = (chartType: ChartType, visBreakdownOptions: VisBreakdownOptions): string => {
  let material = '';
  let usageUnit = '';
  switch (chartType) {
    case 'Concrete Use Intensity by Element':
      material = 'Concrete';
      usageUnit = 'cyd/sf';
      break;
    case 'Steel Use Intensity by Element':
      material = 'Steel';
      usageUnit = 'tons/sf';
      break;
    case 'Timber Use Intensity by Element':
      material = 'Timber';
      usageUnit = 'cft/sf';
      break;
    default:
      return 'Embodied Carbon Intensity (kgCO₂e/m²)';
  }
  switch (visBreakdownOptions.intensity_metric) {
    case 'Use':
      return `${material} Use Intensity (${usageUnit})`;
    case 'Carbon':
      return `${material} Embodied Carbon Intensity (kgCO₂e/m²)`;
  }
}

const determineBoxwhiskersLegend = (chartType: ChartType): string => {
  switch (chartType) {
    case 'Concrete Use Intensity - All Projects':
      return 'Concrete Use Intensity (cyd/sf)';
    case 'Steel Use Intensity - All Projects':
      return 'Steel Use Intensity (tons/sf)';
    case 'Timber Use Intensity - All Projects':
      return 'Timber Use Intensity (cft/sf)';
    default:
      return 'Embodied Carbon Intensity (kgCO₂e/m²)';
  }
}

const orderSeries = (serieA: string, serieB: string): number => {
  const partsA = serieA.split(' ');
  const partsB = serieB.split(' ');

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      let comparableA = partsA[i] || '';
      let comparableB = partsB[i] || '';

      // Compare the values as numbers if neither are NaN
      const numericalA = parseFloat(comparableA);
      const numericalB = parseFloat(comparableB);
      if (!isNaN(numericalA) && !isNaN(numericalB) && numericalA !== numericalB) {
          return numericalA - numericalB;
      }

      // Otherwise compare them as lowercase strings
      const lowercaseA = comparableA.toLowerCase();
      const lowercaseB = comparableB.toLowerCase();
      if (lowercaseA !== lowercaseB) {
          return comparableB.toLowerCase() > comparableA.toLowerCase() ? -1 : 1;
      }
  }

  return 0;
}

const determineDataKeys = (chartType: BarChartType): [ string, string ] => {
  switch (chartType) {
    case 'Embodied Carbon Intensity by Material':
      return [ 'material_category', 'material_type' ];
    default:
      return [ 'overall_structure', 'structure' ];
  }
}

const getDataPointType = (chartType: BoxPlotType | HeatMapType): DataPointType => {
  switch (chartType) {
    case 'Concrete Use Intensity - All Projects':
      return 'Concrete';
    case 'Steel Use Intensity - All Projects':
      return 'Steel';
    case 'Timber Use Intensity - All Projects':
      return 'Timber';
    default:
      return 'GWP';
  }
}

const generateFirmOptions = (firms: string[]): { text: string, value: string }[] => {
  const firmOptions = firms.sort((a, b) => a.toLocaleLowerCase() >= b.toLocaleLowerCase() ? 1 : -1)
                           .map(firm => ({ text: firm, value: firm }));
  return [ { text: 'All Firms', value: '' }, ...firmOptions ];
}

interface ChartState {
  type: ChartType;
  data: any;
  title: string;
}

const MainChart: React.FC = () => {
  const [rawBreakdownData, setRawBreakdownData] = useState<RawBreakdownItem[]>([]);
  const [rawBoxwhiskersData, setRawBoxwhiskersData] = useState<Record<string, RawBoxwhiskersItem[]>>({});
  const [assessmentStage, setAssessmentStage] = useState<AssessmentStageType>("A1-A3");
  const [intensityMetric, setIntensityMetric] = useState<IntensityMetricType>("Use");
  const [projectListType, setProjectListType] = useState<ProjectListType>("All Projects");
  const [fitToOption, setFitToOption] = useState<FitToOptionType>("Zoomed In to Core Distribution");
  const [firmHighlighted, setFirmHighlighted] = useState<string>('');
  const [firmOptions, setFirmOptions] = useState<FirmOptionType[]>([]);
  const [typeGroups, setTypeGroups] = useState<TypeGroup[]>([]);
  const [yearRange, setYearRange] = React.useState<YearRange>({
    start: null,
    end: null
  });

  const [chartState, setChartState] = useState<ChartState>({
    type: "Primary Building Use Type",
    data: null,
    title: "Embodied Carbon Intensity, by Primary Building Use Type"
  });

  const toCanvasRef = useRef<HTMLDivElement>(null);

  const { data: userData, isSuccess: userIsLoaded } = useGetUserQuery();

  const [getVisBoxwhisker, { isLoading: isLoadingBoxwhisker, error: errorBoxwhisker }] = useGetVisBoxwhiskerMutation();
  const [getVisBreakdown, { isLoading: isLoadingBreakdown, error: errorBreakdown }] = useGetVisBreakdownMutation();

  const isLoading = isLoadingBoxwhisker || isLoadingBreakdown;
  const error = isBarChartType(chartState.type) ? errorBreakdown : errorBoxwhisker;

  const visBoxWhiskersOptions: VisBoxwhiskersOptions = useMemo(() => ({
    visualization: isMaterialBoxPlotType(chartState.type) ? 'Primary Building Use Type' : chartState.type,
    data_point_type: getDataPointType(chartState.type),
    assessment_stage: assessmentStage,
    response_data_type: 'dictionary' as 'array' | 'dictionary',
    assessment_year_start: yearRange.start || undefined,
    assessment_year_end: yearRange.end || undefined,
  }), [chartState.type, assessmentStage, yearRange]);

  const visBreakdownOptions: VisBreakdownOptions = useMemo(() => {
    const updatedOptions: VisBreakdownOptions = {
      visualization: 'Element',
      material: null,
      intensity_metric: 'Carbon',
      area_unit: 'm',
      assessment_year_start: yearRange.start || undefined,
      assessment_year_end: yearRange.end || undefined,
    };
    
    if (chartState.type === 'Embodied Carbon Intensity by Material') updatedOptions.visualization = 'Material';

    if (chartState.type === 'Concrete Use Intensity by Element') {
      updatedOptions.material = 'Concrete';
      updatedOptions.intensity_metric = intensityMetric;
    } else if (chartState.type === 'Steel Use Intensity by Element') {
      updatedOptions.material = 'Steel';
      updatedOptions.intensity_metric = intensityMetric;
    } else if (chartState.type === 'Timber Use Intensity by Element') {
      updatedOptions.material = 'Timber';
      updatedOptions.intensity_metric = intensityMetric;
    }
    
    if (updatedOptions.material && intensityMetric === 'Use') {
      updatedOptions.area_unit = 'ft';
    }

    return updatedOptions;
  }, [chartState.type, intensityMetric, yearRange]);

  const fetchBoxwhiskersData = useCallback(async () => {
    if (!userData) return;
    if (!isBoxPlotType(chartState.type) && !isHeatMapType(chartState.type)) return;

    try {
      const result = await getVisBoxwhisker(visBoxWhiskersOptions).unwrap();
      setRawBoxwhiskersData(result);
    } catch (err) {
      console.error("Failed to fetch boxwhiskers data:", err);
    }
  }, [getVisBoxwhisker, visBoxWhiskersOptions, userData, setRawBoxwhiskersData]);

  const fetchBreakdownData = useCallback(async () => {
    if (!userData) return;
    if (!isBarChartType(chartState.type)) return;

    try {
      const result = await getVisBreakdown(visBreakdownOptions).unwrap();
      setRawBreakdownData(result);
    } catch (err) {
      console.error("Failed to fetch breakdown data:", err);
    }
  }, [getVisBoxwhisker, getVisBreakdown, visBreakdownOptions, userData, setRawBreakdownData]);

  const handleBreakdownResponse = useCallback(() => {
    if (!isBarChartType(chartState.type)) return;

    const [ categoryKey, typeKey ] = determineDataKeys(chartState.type);
    
    try {
      const transformedData = transformBreakdownData(rawBreakdownData, categoryKey, typeKey, firmHighlighted);
      
      setTypeGroups(generateTypeGroups(rawBreakdownData, categoryKey, typeKey));
      
      setChartState(prevState => ({
        ...prevState,
        data: transformedData,
        title: determineBreakdownTitle(chartState.type, intensityMetric)
      }));
      
      const firmOptionsSet = rawBreakdownData.reduce(
        (firmSet: Set<string>, item: RawBreakdownItem) => item.firm ? firmSet.add(item.firm) : firmSet,
        new Set()
      );
      setFirmOptions(generateFirmOptions(Array.from(firmOptionsSet)));
    } catch (err) {
      console.error("Failed to handle breakdown response data:", err);
    }
  }, [rawBreakdownData, chartState.type, intensityMetric, firmHighlighted, setChartState, setTypeGroups, setFirmOptions]);

  const handleBoxwhiskerResponse = useCallback(() => {
    if (!isBoxPlotType(chartState.type) && !isHeatMapType(chartState.type)) return;

    try {
      const transformedData = transformBoxwhiskersData(rawBoxwhiskersData, assessmentStage, projectListType, userData, chartState.type, firmHighlighted);

      setChartState(prevState => ({
        ...prevState,
        data: transformedData,
        title: determineBoxwhiskersTitle(chartState.type)
      }));

      const resultValues: RawBoxwhiskersItem[][] = Object.values(rawBoxwhiskersData);
      const firmOptionsSet = resultValues.flat().reduce(
        (firmSet: Set<string>, item: RawBoxwhiskersItem) => item.firm ? firmSet.add(item.firm) : firmSet,
        new Set()
      );
      setFirmOptions(generateFirmOptions(Array.from(firmOptionsSet)));
    } catch (err) {
      console.error("Failed to handle boxwhiskers response data:", err);
    }
  }, [rawBoxwhiskersData, assessmentStage, projectListType, userData, chartState.type, firmHighlighted, setChartState, setFirmOptions]);

  useEffect(() => {
    fetchBoxwhiskersData();
  }, [fetchBoxwhiskersData]);

  useEffect(() => {
    fetchBreakdownData();
  }, [fetchBreakdownData]);

  useEffect(() => {
    handleBoxwhiskerResponse();
  }, [handleBoxwhiskerResponse]);

  useEffect(() => {
    handleBreakdownResponse();
  }, [handleBreakdownResponse]);

  const handleChartTypeChange = (newType: ChartType) => {
    setChartState(prevState => ({
      ...prevState,
      type: newType,
      data: null  // Reset data to trigger loading state
    }));
  };

  const renderMessage = () => (
    <div style={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: chartState.type === "Project Heat Map" ? "absolute" : "relative",
                  zIndex: 1
                }}>
      {error && <Alert severity="error">Error: Please contact a developer</Alert>}
      {isLoading && <CircularProgress size={150} />}
      {!isDataAvailable && !isLoading && !error && userIsLoaded &&
        <Alert severity="warning">{LOW_DATA_MSG}</Alert>
      }
    </div>
  );

  const renderChart = useCallback(() => {
    if (isBarChartType(chartState.type)) {
      return (
        <BarChart
          data={chartState.data || []}
          typeGroups={typeGroups}
          legend={determineBreakdownLegend(chartState.type, visBreakdownOptions)}
        />
      );
    } else if (isBoxPlotType(chartState.type)) {
      return (
        <BoxPlot
          data={chartState.data || []}
          chartType={chartState.type as BoxPlotType}
          legend={determineBoxwhiskersLegend(chartState.type)}
          displayOutliers={fitToOption === 'Zoomed Out to Show Outliers'}
        />
      );
    } else if (isHeatMapType(chartState.type)) {
      return (
        <HeatMap data={chartState.data || []} />
      );
    }
  }, [ typeGroups, chartState, fitToOption ]);

  const renderWatermark = useCallback(() => (
    <Typography
      style={{
        display: 'flex',
        justifyContent: 'center',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%) scaleX(0.3)',
        width: '100%',
        zIndex: 1,
        color: '#1A1A1A',
        opacity: 0.1,
        fontSize: 100,
        lineHeight: 1.4,
        fontFamily: 'sans-serif',
        fontWeight: 300,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        textAlign: 'center',
        pointerEvents: 'none'
      }}
    >
      Data accessible only to SE 2050 signatory firms<br />Not intended for external use
    </Typography>
  ), []);

  const isDataAvailable = useMemo(() => Object.entries(chartState?.data || {}).length !== 0, [chartState.data]);

  const chartHeight = useMemo(() => {
    let height = 500;
    if (!isLoading && isDataAvailable && isBarChartType(chartState?.type ?? '')) {
      const totalGroups = typeGroups.reduce((acc, curr) => acc + curr.subtypes.length, 0);
      height = 64 + Math.max(                       // padding + chart height
        436,                                        // minimum chart height minus padding
        80 + (20 * (chartState.data?.length ?? 0)), // margin + 20 pixels per row
        55 + (20 * totalGroups)                     // axis padding + 20 pixel per type label
      );
    }
    return height;
  }, [isLoading, isDataAvailable, typeGroups]);

  return (
    <div style={{
      marginTop: '24px',
      width: '100%',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0px 1px 8px 0px #0000001F, 0px 3px 4px 0px #00000024, 0px 3px 3px -2px #00000033',
      borderRadius: 4
    }}>
      <div style={{ padding: '32px 32px 0 32px' }}>
        <GroupedSelect
          chartType={chartState.type}
          setChartType={handleChartTypeChange}
          assessmentStage={assessmentStage}
          setAssessmentStage={setAssessmentStage}
          intensityMetric={intensityMetric}
          setIntensityMetric={setIntensityMetric}
          projectListType={projectListType}
          setProjectListType={setProjectListType}
          fitToOption={fitToOption}
          setFitToOption={setFitToOption}
          yearRange={yearRange}
          setYearRange={setYearRange}
          firmHighlighted={firmHighlighted}
          setFirmHighlighted={setFirmHighlighted}
          siteRole={userData?.site_role ?? ''}
          firmOptions={firmOptions}
        />
      </div>

      <div style={{ position: 'relative' }}>
        {!isLoading && !error && isDataAvailable && (
          <ChartMenu chartRef={toCanvasRef} fileName={chartState.type} />
        )}

        <div ref={toCanvasRef} style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '24px 32px 0 32px', textAlign: 'center' }}>
            <Typography variant="h5" component="h2" fontWeight="bold" sx={{ fontSize: 20 }}>
              {chartState.title}
            </Typography>
          </div>

          <div style={{ padding: '16px 32px 32px', position: 'relative', height: chartHeight }}>
            {(error || isLoading || !isDataAvailable) && renderMessage()}

            {!isLoading && !error && isDataAvailable && renderChart()}

            {!isLoading && !error && isDataAvailable && renderWatermark()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainChart;
